import { useEffect } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../lib/auth-store.js';

/**
 * Layout route for authenticated pages. Uses <Outlet /> so children mount on
 * navigation.
 */
export function ProtectedRoute(): React.JSX.Element {
  const status = useAuth((s) => s.status);
  const bootstrap = useAuth((s) => s.bootstrap);
  const resyncAccount = useAuth((s) => s.resyncAccount);

  useEffect(() => {
    if (status === 'idle') void bootstrap();
  }, [status, bootstrap]);

  // The account's role can be changed server-side at any time by an admin.
  // Re-sync when the tab regains focus so permission-gated UI (edit/delete
  // buttons, guards) doesn't keep acting on a stale role after the user was
  // away for a while.
  useEffect(() => {
    if (status !== 'authenticated') return;
    function onVisibilityChange(): void {
      if (document.visibilityState === 'visible') void resyncAccount();
    }
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [status, resyncAccount]);

  if (status === 'idle' || status === 'loading') {
    return <div className="p-8 text-slate-300">Loading…</div>;
  }
  if (status === 'unauthenticated') {
    return <Navigate to="/login" replace />;
  }
  return <Outlet />;
}
